/*
* Copyright  2025-2026, Airspan
* All Rights Reserved
*
* This code is unpublished, confidential and proprietary to Airspan and may only be used in accordance with the appropriate
* license agreement. All other copying, modification, distribution, or
* reverse engineering are strictly prohibited.
*/

import React, { useState } from "react";
import { GridEventListener } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { thresholdProfilesColumns } from "../../../Shared/Table/Columns";
import { DIALOGOPENFOR } from "../../../../globals/globals";

import { IFilter } from "../../../Shared/Filter/types";
import AssociationControl from "../../../Shared/Association/AssociationControl";
import { IFullDeviceDetails } from "../../../../store/api/deviceConfig/types";
import { payloadKeyList } from "../../../Shared/Association/AssociationHelper";
import { setResetFilters, setThresholdAlertResetFilters } from "../../../../store/filters/profiles/ThresholdCrossingDeviceFilterReducers";
import ThresholdCrossingProfileDrawer from "./ThresholdCrossingProfileDrawer";
import { IThresholdCrossingSettingProfile } from "../../../../store/api/admin/types";
import { selectProfilePermissionsSelector } from "../../../../store/api/auth/selectors";
import ProfilePermissions from "../../../../utils/ProfilePermissions";

interface IThresholdCrossingProfile {
    selectedDevice: IFullDeviceDetails;
}

const ThresholdCrossingProfile: React.FC<IThresholdCrossingProfile> = ({ selectedDevice }) => {
    const [selectedProfile, setSelectedProfile] = useState<IThresholdCrossingSettingProfile | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);
    const canManage = !!permissions?.hasOpCodeGroup("performance_management");

    const removeProfileFilters: IFilter = {
        searchTooltip: "Search by device, profile name"
    };

    const addProfileFilters: IFilter = {
        searchTooltip: "Search by device, profile name"
    };
    const entity = { value: selectedDevice.objectKey ?? 0, text: selectedDevice.name };

    const handleRowClicked: GridEventListener<"rowClick"> = (params) => {
        setSelectedProfile(params.row as IThresholdCrossingSettingProfile);
        setIsDrawerOpen(true);
    };

    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
    };

    return (
        <>
            <AssociationControl
                entity={entity}
                parentEntityName={"device"}
                childEntityName={"Profile"}
                associationPermission={canManage}
                listFilters={removeProfileFilters}
                listColumns={thresholdProfilesColumns}
                listSorting={"name, asc"}
                listResetFilterHandler={setResetFilters} 
                listCheckboxSelection={true}
                listDisableColumnMenu={true}
                listDisableRowSelectionOnClick={true}
                listMutationKey={"removeThresholdProfileDevices"}
                listMutationPayloadKey={payloadKeyList.removeThresholdProfileDevices}
                loadListDataKey={"getThresholdCrossingMapProfile"}
                listDialogOpener={DIALOGOPENFOR.ADDTHRESHOLDCROSSINGPROFILE}
                addModelHeaderName={"Add profile"}
                visibleAdd={canManage}
                addButtonName={"Add Profile"}
                showRemoveAction={canManage}
                confirmRemoveDialogOpener={DIALOGOPENFOR.REMOVETHRESHOLDPROFILES}
                removeConfirmationMessage={"Are you sure you want to remove the selected profile from device:"}
                addFilters={addProfileFilters}
                addColumns={thresholdProfilesColumns}
                addSorting={"name, asc"}
                addResetFilterHandler={setThresholdAlertResetFilters}
                showAddAction={false}
                showAddAll={true}
                addCheckboxSelection={true}
                addDisableColumnMenu={true}
                addDisableRowSelectionOnClick={true}
                addMutationKey={"addThresholdProfileDevices"}
                addMutationPayloadKey={payloadKeyList.addThresholdProfileDevices}
                loadAddDataKey={"getThresholdCrossingUnmapProfile"}
                loadAddAllDataKey={"getThresholdCrossingAllUnmapProfile"}
                addShowInfoMessage={false}
                handleRowClicked={handleRowClicked}
            />
            <ThresholdCrossingProfileDrawer
                profile={selectedProfile}
                open={isDrawerOpen}
                onClose={handleDrawerClose}
            />
        </>
    );
};

export default ThresholdCrossingProfile;